import PropTypes from "prop-types"

/**
 * Validators return an oject with the following shape:
 * {
 *   isValid,    // true if valid (required)
 *   hint,       // user-friendly validation message
 *   ...details  // optional type-specific details
 * }
 *
 */

/**
 * Validate generic string
 */
function validateString(value, { minLength = null, maxLength = null }) {
  if (minLength !== null && value.length < minLength) {
    return {
      isValid: false,
      hint: `Too short! At least ${minLength} characters required.`
    }
  } else if (maxLength !== null && value.length > maxLength) {
    return {
      isValid: false,
      hint: `Too long! At most ${maxLength} characters required.`
    }
  } else {
    return { isValid: true }
  }
}

/**
 * Validate email address
 */
function validateEmail(value) {
  const regEx = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i
  if (!regEx.test(value)) {
    return {
      isValid: false,
      hint: 'Expecting something like "john.smith@example.com".'
    }
  } else {
    return { isValid: true }
  }
}

/**
 * Validation types
 * Every type must have a (React) propType, emptyValue, and validator.
 * Note that these base validators have a simpler signature than the
 * custom validators that can be defined in the schema.
 */

const TYPES = {
  string: {
    propType: PropTypes.string,
    emptyValue: "",
    validator: validateString
  },
  email: {
    propType: PropTypes.string,
    emptyValue: "",
    validator: validateEmail
  }
}

/**
 * Validate `values` against `schema`, returning an object with the following
 * structure:
 *
 * [fieldName]: {
 *   {
 *     value,   // the original value or the default value if one defined
 *     isValid, // true if the value is valid
 *     hint     // if not valid, a hint as to what is wrong
 *   }
 * }
 *
 * Schemas have the following structure
 *
 * [fieldName]: {
 *   {
 *     type: 'string',      // JS primative as used by React.PropType
 *     validator: function, // optional custom validator of form ({[fieldName]}) => { return { isValid: false, hint: 'Totally unacceptable!' } }
 *     defaultValue: '',    // optional special value to use when field not provided (can be a function!)
 *     required: true,      // optionally true when field is required
 *     ...validationOptions // optional parameters used by base validator
 *   }
 * }
 *
 * isFormValid will be false if any required fields are missing, unless
 * enforceRequired is false. Similarly, isFormValid will be false if any
 * non-empty fields are invalid unless allowInvalid is true.
 */
export function validate(
  values,
  error,
  schema,
  enforceRequired = true,
  allowInvalid = false,
  isModified
) {
  let data = {}
  let isFormValid = !error || isModified
  Object.getOwnPropertyNames(schema).forEach(fieldName => {
    const { type, defaultValue, required, ...params } = schema[fieldName]
    const emptyValue = TYPES[type].emptyValue
    if (typeof defaultValue === "function") {
      defaultValue()
    }
    const value =
      (values.hasOwnProperty(fieldName)
        ? values[fieldName]
        : typeof defaultValue === "function"
        ? defaultValue()
        : defaultValue) || emptyValue
    const isEmpty = value === emptyValue
    const hasError = error && error.fields && !!error.fields[fieldName]
    const isRequired = required && enforceRequired
    const validation = isEmpty
      ? { isValid: true }
      : TYPES[type].validator(value, params)
    const isValid = validation.isValid && !hasError
    if (
      !(allowInvalid || validation.isValid) ||
      (value === emptyValue && isRequired)
    ) {
      isFormValid = false
    }
    data[fieldName] = {
      value: value,
      error: hasError && error.fields[fieldName],
      isEmpty: isEmpty,
      isRequired: isRequired,
      ...validation,
      isValid: isValid
    }
  })
  Object.getOwnPropertyNames(schema).forEach(fieldName => {
    if (data[fieldName].isValid) {
      const { validator } = schema[fieldName]
      if (validator) {
        const validation = validator(values) // takes all field values but no options
        Object.assign(data[fieldName], validation)
        if (isFormValid) {
          isFormValid = validation.isValid
        }
      }
    }
  })
  return { data, isFormValid }
}

/**
 * Collect `property` for each field defined by schema or `defaultValue`
 * if not declared and `transform` the value before returning an object
 * with the same fields as the schema.
 */
function fields(schema, property, defaultValue = null, transform = x => x) {
  let values = {}
  Object.getOwnPropertyNames(schema).forEach(fieldName => {
    const fieldInfo = schema[fieldName]
    if (property && fieldInfo.hasOwnProperty(property)) {
      values[fieldName] = transform(fieldInfo[property])
    } else {
      values[fieldName] = transform(defaultValue)
    }
  })
  return values
}

/**
 * ValidatedForm is a higher-order component for creating self-validating
 * pure/stateless form components compatible with the FormView interface.
 */
export default function ValidatedForm({
  schema,
  render,
  customPropTypes = {}
}) {
  function Form(props) {
    const {
      values,
      error,
      enforceRequired,
      allowInvalid,
      isModified,
      ...rest
    } = props
    const { data, isFormValid } = validate(
      values,
      error,
      schema,
      enforceRequired,
      allowInvalid,
      isModified
    )
    return render({ data, isFormValid, error, isModified, ...rest })
  }

  Form.propTypes = {
    values: PropTypes.shape(
      fields(schema, "type", null, type => TYPES[type].propType)
    ),
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        message: PropTypes.string
        // fields: fields(schema, null, PropTypes.string)
      })
    ]),
    isPending: PropTypes.bool,
    onChange: PropTypes.func,
    onSubmit: PropTypes.func,
    allowInvalid: PropTypes.bool,
    enforceRequired: PropTypes.bool,
    ...customPropTypes
  }

  Form.defaultProps = {
    error: false,
    isPending: false,
    onChange: (event, data) => console.log(event, data),
    onSubmit: event => console.log(event),
    allowInvalid: false,
    enforceRequired: true
  }

  return Form
}
